import { AutoComplete, Form, Modal, Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IBorrower from '~Api/Application/IBorrower';
import IConditionSuggestion from '~Api/Application/IConditionSuggestion';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import {
    applicationConditionSuggestionsListAction,
    applicationConditionsAddAction,
} from '~Applications/actions';
import { applicationConditionSuggestionsSelector } from '~Applications/selectors';
import { workflowStatusLabels } from '~Applications/workflowStatusLabels';
import { IGlobalState } from '~reducer';

interface IOption {
    label: string;
    score: number;
    value: string;
}

interface IConditionAddErrors {
    conditionName?: string;
}

export default function ConditionAddModal(props: {
    application?: IApplication;
    applicationProperty?: IApplicationProperty;
    applicationBorrower?: IBorrower;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const {
        application,
        applicationBorrower,
        applicationProperty,
        isOpen,
        onCancel,
    } = props;

    const [ conditionName, setConditionName ] = useState<string>(null);
    const [ options, setOptions ] = useState<IOption[]>([]);
    const [ requiredWorkflowStatus, setRequiredWorkflowStatus ] = useState<WorkflowStatusEnum>(WorkflowStatusEnum.FormalApproval);
    const [ errors, setErrors ] = useState<IConditionAddErrors>({});

    const applicationBorrowerUuid: string = applicationBorrower ? applicationBorrower.uuid : null;
    const applicationPropertyUuid: string = applicationProperty ? applicationProperty.uuid : null;
    const applicationUuid: string = application ? application.uuid : null;
    const entityUuid: string = applicationPropertyUuid || applicationBorrowerUuid || applicationUuid;

    const suggestions: IConditionSuggestion[] = useSelector((state: IGlobalState): IConditionSuggestion[] => applicationConditionSuggestionsSelector(state, entityUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!suggestions) {
            dispatch(applicationConditionSuggestionsListAction(
                applicationBorrowerUuid,
                applicationPropertyUuid,
                applicationUuid,
            ));
        }
    }, [
        applicationBorrowerUuid,
        applicationPropertyUuid,
        applicationUuid,
        dispatch,
        suggestions,
    ]);

    const validateConditionName: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (conditionName === null || conditionName === '') {
            error = 'Please enter a name';
        } else if (conditionName.length > 255) {
            error = 'Name must be less than 255 characters';
        }

        setErrors((prevErrors: IConditionAddErrors): IConditionAddErrors => ({
            ...prevErrors,
            conditionName: error,
        }));

        return !error;
    }, [
        conditionName,
    ]);

    const onClickAdd: () => void = useCallback((): void => {
        let valid: boolean = true;

        valid = validateConditionName() && valid;

        if (!valid) {
            return;
        }

        dispatch(applicationConditionsAddAction(
            applicationUuid,
            applicationBorrowerUuid,
            applicationPropertyUuid,
            conditionName,
            requiredWorkflowStatus,
        ));

        setConditionName(null);
        setRequiredWorkflowStatus(WorkflowStatusEnum.FormalApproval);
        onCancel();
    }, [
        applicationBorrowerUuid,
        applicationPropertyUuid,
        applicationUuid,
        conditionName,
        dispatch,
        onCancel,
        requiredWorkflowStatus,
        validateConditionName,
    ]);

    const onSearchConditionName: (value: string) => void = useCallback((value: string): void => {
        const searchValue: string = value.toLocaleLowerCase();

        const results: IOption[] = [];

        _.each(suggestions, (suggestion: IConditionSuggestion): void => {
            if (suggestion.name.toLocaleLowerCase().includes(searchValue)) {
                results.push({
                    label: suggestion.name,
                    score: suggestion.score,
                    value: suggestion.name,
                });
            }
        });

        setConditionName(value);
        setOptions(_.orderBy(results, [ 'score' ], 'desc'));
    }, [
        suggestions,
    ]);

    const onSelectConditionName: (value: string) => void = useCallback((value: string): void => {
        setConditionName(value);
    }, []);

    const onSelectWorkflowStatus: (requiredWorkflowStatus: WorkflowStatusEnum) => void = useCallback((requiredWorkflowStatus: WorkflowStatusEnum): void => {
        setRequiredWorkflowStatus(requiredWorkflowStatus);
    }, []);

    const workflowStatusOptions: DefaultOptionType[] = _.map(workflowStatusLabels, (label: string, workflowStatus: string): DefaultOptionType => ({
        label,
        value: workflowStatus,
    }));

    if (!suggestions) {
        return null;
    }

    const entityName: string = applicationProperty ? applicationProperty.dealProperty.formattedAddress : (applicationBorrower ? applicationBorrower.dealBorrower.formattedName : application.code);

    return (
        <Modal
            destroyOnClose={true}
            okText='Add Condition'
            onCancel={onCancel}
            onOk={onClickAdd}
            open={isOpen}
            title={`Add Condition for ${entityName}`}
            wrapClassName='application-conditions-add-modal'
            width={800}
        >
            <Form.Item
                label='Name'
                className='condition'
                extra={`${conditionName ? conditionName.length : 0} / 255`}
                help={errors.conditionName}
                validateStatus={errors.conditionName && 'error'}
            >
                <AutoComplete
                    dropdownMatchSelectWidth={300}
                    maxLength={255}
                    onBlur={validateConditionName}
                    onSearch={onSearchConditionName}
                    onSelect={onSelectConditionName}
                    options={_.slice(options, 0, 20)}
                    value={conditionName}
                />
            </Form.Item>
            <Form.Item
                label='Required Before'
                className='workflow-status'
            >
                <Select
                    onChange={onSelectWorkflowStatus}
                    value={requiredWorkflowStatus}
                    options={workflowStatusOptions}
                />
            </Form.Item>
        </Modal>
    );
}
